import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useTheme,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { getAPI } from "../api/callAPI";
import moment from "moment";

const dayAry = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const dayNames = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

const CollapsibleWorkingHours = ({ data, tab = 0, expanded, onChange }) => {
  const { palette } = useTheme();

  const workingHours = useMemo(() => {
    const tabName = tab === 0 ? "bar" : "kithen";
    const result = {};

    if (data && data[tabName]) {
      const tabData = data[tabName];
      dayAry.forEach((d) => {
        if (tabData[d]) {
          result[dayNames[d]] = tabData[d];
        }
      });
    }

    return result;
  }, [tab, data]);

  const today = moment().format("dddd");
  const todayTimings = workingHours?.[today] || [];

  if (!workingHours || Object.keys(workingHours).length === 0) return null;

  const renderTiming = (timings) =>
    timings?.map((timing, index) => (
      <Typography color="white" key={index}>
        {`${moment(timing.opening_time, "HH:mm:ss").format(
          "hh:mm a"
        )} - ${moment(timing.closing_time, "HH:mm:ss").format("hh:mm a")}`}
      </Typography>
    ));

  return (
    <Accordion expanded={expanded} onChange={onChange}>
      <AccordionSummary sx={{ backgroundColor: palette.primary.main }}>
        <Box
          className="accordian-main"
          display="flex"
          justifyContent="space-between"
          width="100%"
        >
          <Box display="flex" alignItems="center">
            <AccessTimeIcon sx={{ color: palette.common.white, mr: 1 }} />
            <Typography variant="body1" color={palette.common.white}>
              {tab === 0 ? "Bar Opening Hours" : "Kitchen Opening Hours"}
            </Typography>
          </Box>
          {expanded ? (
            <ExpandLessIcon sx={{ color: palette.common.white }} />
          ) : (
            <ExpandMoreIcon sx={{ color: palette.common.white }} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: palette.primary.main }}>
        <Box>
          {/* Today */}
          <Box
            display="flex"
            justifyContent="space-between"
            px={1}
            py={0.5}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              color: "black",
            }}
          >
            <Typography
              color="white"
              className="days-timing"
            >{`Today (${today})`}</Typography>
            <Box>{renderTiming(todayTimings)}</Box>
          </Box>

          {/* Monday - Saturday */}
          <Box
            display="flex"
            justifyContent="space-between"
            px={1}
            py={0.5}
            sx={{ borderRadius: 1 }}
          >
            <Typography color="white" className="days-timing">
              Monday - Saturday
            </Typography>
            <Box>{renderTiming(workingHours["Monday"])}</Box>
          </Box>

          {/* Sunday */}
          {workingHours["Sunday"] && (
            <Box
              display="flex"
              justifyContent="space-between"
              px={1}
              py={0.5}
              sx={{ borderRadius: 1 }}
            >
              <Typography color="white">Sunday</Typography>
              <Box>{renderTiming(workingHours["Sunday"])}</Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const OpeningHours = ({ store }) => {
  const { endpoint } = useSelector((state) => state.auth);
  const [expandedPanel, setExpandedPanel] = useState("bar");
  const [loading, setLoading] = useState(true);
  const [isError, setError] = useState(false);
  const [workingHoursData, setWorkingHoursData] = useState(null);

  const getHours = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAPI(endpoint, `bar-kitchen-hours/${store?.id}`);
      if (resp?.success && resp?.data) {
        setWorkingHoursData(resp.data);
      }
    } catch (error) {
      const errorMsg = get(
        error,
        "response.data.message",
        "Something went wrong"
      );
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [endpoint, store?.id]);

  useEffect(() => {
    getHours();
  }, [getHours]);

  if (loading || isError) return null;

  return (
    <Grid container mt={4}>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} sx={{ p: 2 }}>
        <CollapsibleWorkingHours
          data={workingHoursData}
          tab={0}
          expanded={expandedPanel === "bar"}
          onChange={() =>
            setExpandedPanel(expandedPanel === "bar" ? false : "bar")
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} sx={{ p: 2 }}>
        <CollapsibleWorkingHours
          data={workingHoursData}
          tab={1}
          expanded={expandedPanel === "kitchen"}
          onChange={() =>
            setExpandedPanel(expandedPanel === "kitchen" ? false : "kitchen")
          }
        />
      </Grid>
    </Grid>
  );
};

export default OpeningHours;
